import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  CircularProgress,
  Card,
  CardContent,
  IconButton
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import GetAppIcon from '@material-ui/icons/GetApp';
import { solicitudDetalleService } from '../../services';

const DEFAULT_AUTH_API_URL = "http://localhost:8080";

const getAuthApiUrl = () => {
  if (process.env.NODE_ENV === 'development') {
    return ''; // Use relative path for proxy in development
  }
  const raw =
    (typeof window !== 'undefined' && window.env?.REACT_APP_AUTH_API_URL) ||
    (typeof window !== 'undefined' && window.env?.REACT_APP_API_URL) ||
    process.env.REACT_APP_AUTH_API_URL ||
    process.env.REACT_APP_API_URL ||
    DEFAULT_AUTH_API_URL;
  return raw.endsWith("/") ? raw.slice(0, -1) : raw;
};

const AUTH_API_URL = getAuthApiUrl();

const initialFormData = {
  estatutoId: null,
  balanceId: null,
  accionistaId: null,
  poderActaId: null,
  poderId: null,
  ddjjGananciasId: null
};

const initialFiles = {
  estatuto: null,
  balance: null,
  accionista: null,
  poderActa: null,
  poder: null,
  ddjjGanancias: null
};

const REQUIRED_FIELDS = ['estatutoId', 'balanceId', 'accionistaId'];

const SeccionDocumentacionRespaldatoria = ({ solicitudId }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState(initialFormData);
  const [files, setFiles] = useState(initialFiles);
  const [uploadingField, setUploadingField] = useState('');

  const fetchArchivoInfo = useCallback(async (archivoId, fieldKey) => {
    if (!archivoId) return;

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${AUTH_API_URL}/api/archivos/${archivoId}`, {
        method: 'GET',
        headers: {
          'Accept': '*/*',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        
        // Convertir archivoData (base64) a blob
        const binaryString = atob(result.archivoData);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes]);
        const fileUrl = URL.createObjectURL(blob);
        
        setFiles((prev) => ({
          ...prev,
          [fieldKey]: {
            id: archivoId,
            name: result.nombre || 'Documento.pdf',
            size: blob.size || 0,
            url: fileUrl
          }
        }));
      }
    } catch (err) {
      console.error(`Error obteniendo archivo ${fieldKey}:`, err);
    }
  }, []);

  const cargarDatos = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await solicitudDetalleService.getDocumentacionRespaldatoriaEmpresa(solicitudId);
      if (response) {
        const data = {
          estatutoId: response.estatutoId || null,
          balanceId: response.balanceId || null,
          accionistaId: response.accionistaId || null,
          poderActaId: response.poderActaId || null,
          poderId: response.poderId || null,
          ddjjGananciasId: response.ddjjGananciasId || null
        };
        setFormData(data);

        await Promise.all([
          fetchArchivoInfo(data.estatutoId, 'estatuto'),
          fetchArchivoInfo(data.balanceId, 'balance'),
          fetchArchivoInfo(data.accionistaId, 'accionista'),
          fetchArchivoInfo(data.poderActaId, 'poderActa'),
          fetchArchivoInfo(data.poderId, 'poder'),
          fetchArchivoInfo(data.ddjjGananciasId, 'ddjjGanancias')
        ]);
      }
    } catch (err) {
      setError('Error al cargar la documentación respaldatoria');
    } finally {
      setLoading(false);
    }
  }, [solicitudId, fetchArchivoInfo]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  const handleFileUpload = async (event, fieldKey, formField) => {
    const filesSelected = Array.from(event.target.files || []);
    if (filesSelected.length === 0) return;

    const file = filesSelected[0];
    
    // Validar tamaño del archivo (10 MB = 10 * 1024 * 1024 bytes)
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
    if (file.size > MAX_FILE_SIZE) {
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
      setError(`El archivo "${file.name}" excede el tamaño máximo permitido de 10 MB. Tamaño actual: ${fileSizeMB} MB.`);
      event.target.value = null; // Limpiar el input
      return;
    }
    
    setUploadingField(fieldKey);
    setError('');

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const token = localStorage.getItem("token");
      const response = await fetch(`${AUTH_API_URL}/api/archivos/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataUpload
      });

      if (!response.ok) {
        throw new Error('Error subiendo el archivo');
      }

      const result = await response.json();
      const archivoId = result.id || result.archivoId;

      if (!archivoId) {
        throw new Error('No se pudo obtener el ID del archivo');
      }

      setFormData((prev) => ({
        ...prev,
        [formField]: archivoId
      }));

      setFiles((prev) => ({
        ...prev,
        [fieldKey]: {
          id: archivoId,
          name: file.name,
          size: file.size
        }
      }));
    } catch (err) {
      console.error(`Error subiendo archivo ${fieldKey}:`, err);
      setError('Error al subir el archivo. Intente nuevamente.');
    } finally {
      setUploadingField('');
      event.target.value = null;
    }
  };

  const handleRemoveFile = (fieldKey, formField) => {
    setFiles((prev) => ({
      ...prev,
      [fieldKey]: null
    }));
    setFormData((prev) => ({
      ...prev,
      [formField]: null
    }));
  };

  const isFormValid = () => {
    const hasRequired = REQUIRED_FIELDS.every((field) => formData[field]);
    const hasPoder = formData.poderActaId || formData.poderId;
    return hasRequired && hasPoder;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    if (!isFormValid()) {
      setError('Debe cargar los documentos obligatorios antes de continuar.');
      setSaving(false);
      return;
    }

    try {
      const datosParaGuardar = {
        solicitudId: parseInt(solicitudId),
        ...formData
      };
      await solicitudDetalleService.saveDocumentacionRespaldatoriaEmpresa(datosParaGuardar);
      setSuccess('Datos guardados exitosamente');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Error al guardar los datos');
    } finally {
      setSaving(false);
    }
  };

  const renderFileCard = (label, fieldKey, formField, required = false, helperText = '') => {
    const fileInfo = files[fieldKey];
    const inputId = `${fieldKey}-upload`;

    return (
      <Card style={{ marginBottom: '1.5rem', border: '1px solid #e0e0e0', backgroundColor: '#f9fafb' }} key={fieldKey}>
        <CardContent>
          <Box display="flex" flexDirection="column" alignItems="flex-start" gap={1}>
            <Typography variant="h6" style={{ fontWeight: 'bold' }}>
              {label} {required && <span style={{ color: '#f44336' }}>*</span>}
            </Typography>
            {helperText && (
              <Typography variant="body2" color="textSecondary">
                {helperText}
              </Typography>
            )}
            <input
              id={inputId}
              type="file"
              style={{ display: 'none' }}
              onChange={(event) => handleFileUpload(event, fieldKey, formField)}
            />
            <Box display="flex" gap={1} alignItems="center">
              <label htmlFor={inputId}>
                <Button
                  variant="contained"
                  component="span"
                  disabled={uploadingField === fieldKey || saving}
                  color="primary"
                >
                  {fileInfo ? 'Reemplazar archivo' : 'Subir archivo'}
                </Button>
              </label>
              {uploadingField === fieldKey && <CircularProgress size={20} />}
              {fileInfo && (
                <IconButton
                  aria-label="Quitar archivo"
                  onClick={() => handleRemoveFile(fieldKey, formField)}
                  disabled={saving}
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </Box>
            {fileInfo && (
              <Box display="flex" flexDirection="column" marginTop={1}>
                <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                  {fileInfo.name}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {Math.round((fileInfo.size || 0) / 1024)} KB
                </Typography>
                {fileInfo.url && (
                  <Box display="flex" gap={1} marginTop={1}>
                    <Button
                      variant="text"
                      color="primary"
                      onClick={() => window.open(fileInfo.url, '_blank')}
                      size="small"
                    >
                      Ver documento
                    </Button>
                    <Button
                      variant="text"
                      color="primary"
                      startIcon={<GetAppIcon />}
                      onClick={() => {
                        const link = document.createElement('a');
                        link.href = fileInfo.url;
                        link.download = fileInfo.name || 'documento.pdf';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      }}
                      size="small"
                    >
                      Descargar
                    </Button>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  if (loading && !files.estatuto && !files.balance && !files.accionista) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Documentación Respaldatoria
        </Typography>
        {error && (
          <Box p={2} mb={2} style={{ backgroundColor: '#ffebee', color: '#c62828', borderRadius: '4px' }}>
            <Typography variant="body2">{error}</Typography>
          </Box>
        )}
        {success && (
          <Box p={2} mb={2} style={{ backgroundColor: '#e8f5e9', color: '#2e7d32', borderRadius: '4px' }}>
            <Typography variant="body2">{success}</Typography>
          </Box>
        )}
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              {renderFileCard('Estatuto Social', 'estatuto', 'estatutoId', true)}
            </Grid>
            <Grid item xs={12}>
              {renderFileCard('Balance', 'balance', 'balanceId', true)}
            </Grid>
            <Grid item xs={12}>
              {renderFileCard('Listado de Accionistas', 'accionista', 'accionistaId', true)}
            </Grid>
            <Grid item xs={12}>
              {renderFileCard(
                'Designación Directorio',
                'poderActa',
                'poderActaId',
                !formData.poderId
              )}
            </Grid>
            <Grid item xs={12}>
              {renderFileCard(
                'Poder',
                'poder',
                'poderId',
                !formData.poderActaId
              )}
            </Grid>
            <Grid item xs={12}>
              {renderFileCard('Declaración Jurada de Ganancias', 'ddjjGanancias', 'ddjjGananciasId', false)}
            </Grid>
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={saving || uploadingField !== '' || !isFormValid()}
                fullWidth
              >
                {saving ? <CircularProgress size={20} /> : 'Guardar'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </CardContent>
    </Card>
  );
};

export default SeccionDocumentacionRespaldatoria;

